import React, {useMemo} from 'react';
import useOptimizedConfig from '@/hooks/useOptimizedConfig';
import i18n from 'i18n';

import LineChartWrapper from './line/LineChartWrapper';
import AreaChartWrapper from './area/AreaChartWrapper';
import PieChartWrapper from './pie/PieChartWrapper';
import SunburstChartWrapper from './sunburst/SunburstChartWrapper';
import TreemapChartWrapper from './treemap/TreemapChartWrapper';
import BarChartWrapper from './bar/BarChartWrapper';

import {ChartFieldToQueryOption, PartialChartQuery, BarOrientation, BarSortOrder, DataLabelPosition} from 'entities/chart';
import {chartHeightValues, chartWidthValues, EchartsChartType} from '@/views/storyboard/utils/chartConstants';
import {ChartResponseData} from 'entities/chartData';
import {ChartFooterData} from '@/views/storyboard/charts/utils/chartFooterData';


export interface ChartComponentProps {
    dataLooselyTyped: ChartResponseData;
    chartQuery: PartialChartQuery;
    height?: typeof chartHeightValues[number];
    width?: typeof chartWidthValues[number];
    title?: string;
    description?: string;
    datePeriod?: string;
    linearRegression?: boolean;
    comparablePeriod?: boolean;
    movingAverageDays?: number | null;
    movingAverageDaysCenter?: boolean;
    scalingFactor?: string;
    barOrientation?: BarOrientation;
    barSortOrder?: BarSortOrder;
    dataLabelPosition?: DataLabelPosition;
    chartRef?: any; // TODO
    previewOnly?: boolean;
    fieldToQuery?: ChartFieldToQueryOption;
    searchViewCheck?: boolean;
    queryDuration?: string;
    chartSettingsView?: boolean;
    chartId?: number;
    onPreviewFooterChange?: (footerData: ChartFooterData) => void;
}

interface Props extends Omit<ChartComponentProps, (
    'dataLooselyTyped'
    | 'linearRegression'
    | 'comparablePeriod'
    | 'movingAverageDays'
    | 'movingAverageDaysCenter'
    | 'scalingFactor'
    | 'barOrientation'
    | 'barSortOrder'
    | 'dataLabelPosition'
)> {
    data: ChartResponseData;
    type: EchartsChartType;
    linearRegression?: boolean;
    comparablePeriod?: boolean;
    movingAverageDays?: number | null;
    movingAverageDaysCenter?: boolean;
    scalingFactor?: string;
    barOrientation?: BarOrientation;
    barSortOrder?: BarSortOrder;
    dataLabelPosition?: DataLabelPosition;
    chartId?: number;
}

const chartComponents: Record<EchartsChartType, React.FC<ChartComponentProps>> = {
    [EchartsChartType.AreaChart]: AreaChartWrapper,
    [EchartsChartType.BarChart]: BarChartWrapper,
    [EchartsChartType.LineChart]: LineChartWrapper,
    [EchartsChartType.PieChart]: PieChartWrapper,
    [EchartsChartType.SunburstChart]: SunburstChartWrapper,
    [EchartsChartType.TreemapChart]: TreemapChartWrapper,
};

const ChartContainer = React.memo<Props>((
    {
        type,
        data,
        width,
        height,
        title = '',
        description = '',
        linearRegression = false,
        comparablePeriod = true,
        movingAverageDays = null,
        movingAverageDaysCenter = true,
        scalingFactor = '',
        barOrientation = BarOrientation.Vertical,
        barSortOrder = BarSortOrder.HighToLow,
        dataLabelPosition = DataLabelPosition.Off,
        chartQuery,
        chartRef,
        previewOnly,
        searchViewCheck,
        queryDuration = '',
        chartSettingsView = false,
        chartId,
        onPreviewFooterChange,
    },
) => {
    const {userData} = useOptimizedConfig();
    const datePeriod = useMemo(() => {
        const start = userData?.sessionStartDate?.trim();
        const end = userData?.sessionEndDate?.trim();
        if (!start || !end) {
            return '';
        }

        return `${i18n.t('dates.text')}: ${start}-${end}`;
    }, [userData?.sessionStartDate, userData?.sessionEndDate]);
    const Component: React.FC<ChartComponentProps> = chartComponents[type];

    return (
        <Component
            key={type}
            dataLooselyTyped={data}
            width={width}
            height={height}
            title={title}
            description={description}
            datePeriod={datePeriod}
            linearRegression={linearRegression}
            comparablePeriod={comparablePeriod}
            movingAverageDays={movingAverageDays}
            movingAverageDaysCenter={movingAverageDaysCenter}
            scalingFactor={scalingFactor}
            barOrientation={barOrientation}
            barSortOrder={barSortOrder}
            dataLabelPosition={dataLabelPosition}
            chartQuery={chartQuery}
            chartRef={chartRef}
            previewOnly={previewOnly}
            searchViewCheck={searchViewCheck}
            queryDuration={queryDuration}
            chartSettingsView={chartSettingsView}
            chartId={chartId}
            onPreviewFooterChange={onPreviewFooterChange}
        />
    );
});


export default ChartContainer;